/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { ToastContainer, toast } from 'react-toastify';

import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Modal from '@mui/material/Modal';
import DialogContent from '@mui/material/DialogContent';
import { useLazyQuery, useMutation } from '@apollo/client';
import { MdEdit } from 'react-icons/md';
import { IoTrashSharp } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import ModalDetail from '../../../../ModalDetail/ModalDetail';
import Filters from '../Filters/Filters';
import Search from '../Search/Search';
import CodeMappingGrid from '../Grid/CodeMappingGrid';

import * as Q from '../../../../../constants/graphqlQueries';

import 'react-toastify/dist/ReactToastify.css';
import UserRoles from '../../../../../constants/userRoles';
import transactionResponse from '../../../../../constants/transactionResponse';
import MappedCodesDetail from './MappedCodesDetail';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const transactionMessages = {
  moduleName: 'Mapping',
};
const codeMappingTypes = {
  ACD: 'ACD',
  schedule: 'Schedule',
  container: 'Container',
  segment: 'Segment'
};

const SearchCodeMapping = ({ handleEditCodeMapping, mappingResponse }) => {
  useStyles();
  // lazy queries
  const [getCodeMappings, { data: dataCodeMappings, loading: isLoadingCodeMappings, refetch: refetchCodeMappings }] = useLazyQuery(
    Q.CODE_MAPPINGS_QUERY,
  );
  const [findMappingAssociation, { data: dataMappingAssociation, loading: loadingMapping }] = useLazyQuery(Q.CODE__MAPPING__FIND__ASSOCIATION);
  const [deleteCodeMapping, { data: deleteReply, loading: loadingReply }] = useMutation(Q.CODE_MAPPING_DELETE);
  const [deleteACDCodeMapping, { data: deleteACDReply, loading: loadingACDReply }] = useMutation(Q.ACD_CODE_MAPPING_DELETE);

  const user = useSelector((state) => state.User);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState(codeMappingTypes.ACD);
  const [openModal, setOpenModal] = useState(false);
  const [openModalRestricted, setOpenModalRestricted] = useState(false);
  const [codeMappingIdToDelete, setCodeMappingIdToDelete] = useState();
  const [codeMappingToDestroy, setCodeMappingToDestroy] = useState('');
  const [associatedRule, setAssociatedRule] = useState();

  const [refetchSearch, setRefetchSearch] = useState(false);

  const eraseMapping = (codeMapping) => {
    let searchPosition = 3;
    if (codeMapping.CodeMappingType === codeMappingTypes.ACD) {
      searchPosition = 1;
    } 
    if (codeMapping.CodeMappingType === codeMappingTypes.schedule && codeMapping.ScheduleType === codeMappingTypes.container) {
      searchPosition = 2;
    }

    setCodeMappingToDestroy(codeMapping);
    setCodeMappingIdToDelete(codeMapping.CodeMappingId);

    findMappingAssociation({ variables: { CodeMappingId: codeMapping.CodeMappingId, Position: searchPosition } })
  }

  const gridConfiguration = {
    keyGrid: 1,
    columns: [
      { title: 'Code Type', field: 'CodeMappingType', width: '15%' },
      { title: 'Code Label', field: 'Label', width: '30%' },
      { title: 'Code Description', field: 'Description', width: '30%' },
      { title: 'Schedule Type', field: 'ScheduleType', width: '25%', render: rowData => rowData.ScheduleType ? rowData.ScheduleType : 'N/A' }
      ],
    options: {
      defaultExpanded: true,
      filtering: false,
      actionsColumnIndex: 0,
      tableLayout: 'auto',
      actionsCellStyle: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
      },
    },
    localization: {
      header: {
        actions: 'Actions',
      },
      body: {
        emptyDataSourceMessage: 'Search for code mappings you would like to see',
      },
    },
    actions: [
      {
        disabled: user.Role === UserRoles.WFM && !(user.Claims.CodeMappingEnterpriseEdit) && user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner,
        icon: () => (
          <MdEdit style={{
            height: 20, width: 20, searchPosition: 'relative', marginTop: '0.4rem', padding: '0.1rem',
          }}
          />
        ),
        tooltip: <span className='action-tooltip'>Edit</span>,
        onClick: (e, { CodeMappingId, CodeMappingType }) => handleEditCodeMapping(CodeMappingId, CodeMappingType),
      },
      {
        disabled: user.Role === UserRoles.WFM && !(user.Claims.CodeMappingEnterpriseDelete) && user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner,
        icon: () => (
          <IoTrashSharp style={{
            height: 20, width: 20, searchPosition: 'relative', marginTop: '0.4rem', padding: '0.1rem',
          }}
          />
        ),
        tooltip: <span className='action-tooltip'>Delete</span>,
        onClick: (e, rowData) => eraseMapping(rowData),
      }
    ],
    detailPanel: rowData => <MappedCodesDetail data={rowData} />
  };

  const modalOption = {
    title: 'Confirm Action',
    description: `Do you want to delete ${codeMappingToDestroy.Label}? This action is permanent`,
  }
  const modalRestrictOption = {
    title: 'Restricted action',
    description: 'The following code mapping cannot be deleted because it is associated with the following existing rule(s):',
  }

  const onSearchClick = (terms) => {
    getCodeMappings({
      variables: {
        input: {
          codeMappingType: filter,
          codeLabel: terms.codeLabel,
          ACDState: terms.ACDState,
          scheduleType: terms.scheduleType,
        }
      },
    });
  };

  const onClearClick = () => {
    setData([]);
  };

  const onFilterClick = (_filter) => {
    setFilter(_filter);
  };

  const onDelete = () => {
    if(codeMappingToDestroy.CodeMappingType === codeMappingTypes.schedule) deleteCodeMapping({ variables: { CodeMappingId: codeMappingIdToDelete } });
    if(codeMappingToDestroy.CodeMappingType === codeMappingTypes.ACD) deleteACDCodeMapping({ variables: { ACDCodeMappingId: codeMappingIdToDelete } });
    setOpenModal(false);
  };

  const handleCloseDeleteModal = () => {
    setOpenModal(false);
  }
  const handleCloseRestrictModal = () => {
    setOpenModalRestricted(false);
  }

  useEffect(() => {
    if (dataCodeMappings) {
      if (dataCodeMappings.codeMappingsScheduleACD.length) setData(dataCodeMappings.codeMappingsScheduleACD);
      else toast(transactionResponse(null, null, 'dataNotFound'));
    }
  }, [dataCodeMappings]);
  useEffect(() => {
    if (mappingResponse) {
      toast(transactionResponse(transactionMessages.moduleName, mappingResponse));
    }
  }, [mappingResponse]);
  useEffect(() => {
    if (deleteReply || deleteACDReply) {
      const replyStatus = deleteReply ? deleteReply.deleteCodeMapping.status : deleteACDReply.deleteACDCodeMapping.status;
      toast(transactionResponse(transactionMessages.moduleName, replyStatus));
      refetchCodeMappings();
      setRefetchSearch(true);
    }
  }, [deleteReply, deleteACDReply]);
  useEffect(() => {
    if (dataMappingAssociation) {
      if (dataMappingAssociation.findAssociation.length) {
        setAssociatedRule(dataMappingAssociation.findAssociation);
        setOpenModalRestricted(true);
      }
      else {
        setOpenModal(true);
      }
    }
  }, [dataMappingAssociation]);

  return (
    <>
      <Box className='__codeMapping'>
      <div className='__codeMapping__filters'>
        <Filters
          onFilterClick={onFilterClick}
        />
        <Search
          onSearchClick={onSearchClick}
          filter={filter}
          onClearClick={onClearClick} 
          refetchSearch={refetchSearch} 
        />
      </div>
      <div className='__codeMapping__grid'>
        <Box className='__codeMapping__grid--container'>
          <Box className='__codeMapping__grid--container-title'>
            Mapped Codes
          </Box>
        </Box>
        <CodeMappingGrid
          className='__codeMapping__grid--container__CodeMappingGrid'
          keyGrid={gridConfiguration.keyGrid}
          columns={gridConfiguration.columns}
          actions={gridConfiguration.actions}
          detailPanel={gridConfiguration.detailPanel}
          options={gridConfiguration.options}
          localization={gridConfiguration.localization}
          isLoading={isLoadingCodeMappings || loadingReply || loadingACDReply || loadingMapping}
          data={data.map((o) => ({ ...o }))} />
      </div>
      </Box>
      <Modal
        aria-labelledby='deleted-modal-title'
        aria-describedby='deleted-modal-description'
        open={openModal}
        onClose={handleCloseDeleteModal}
      >
        <DialogContent>
          <ModalDetail modalOptions={modalOption}>
            <button className='simple-modal-btn' type='button' onClick={onDelete}>Delete</button>
            <button className='simple-modal-btn' type='button' onClick={handleCloseDeleteModal}>Cancel</button>
          </ModalDetail>
        </DialogContent>
      </Modal>
      <Modal
        aria-labelledby='restricted-modal-title'
        aria-describedby='restricted-modal-description'
        open={openModalRestricted}
        onClose={handleCloseRestrictModal}
      >
        <DialogContent>
          <ModalDetail modalOptions={modalRestrictOption}>
            <div className='restricted-modal-container'>
              <ul className='restricted-modal-list'>
                {associatedRule && associatedRule.map(rule =>
                  <li key={rule.RuleId}>
                    {rule.RuleName}
                  </li>
                )}
              </ul>
            </div>
            <button className='simple-modal-btn' type='button' onClick={handleCloseRestrictModal}>Ok</button>
          </ModalDetail>
        </DialogContent>
      </Modal>
      <ToastContainer hideProgressBar/>
    </>
  );
};

SearchCodeMapping.propTypes = {
  handleEditCodeMapping: PropTypes.func.isRequired,
  mappingResponse: PropTypes.string
};

SearchCodeMapping.defaultProps = {
  mappingResponse: null
};
export default SearchCodeMapping;
